import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  CalendarDays,
  Clock,
  FileText,
  Flame,
  Import,
  Moon,
  PenLine,
  TextSelect,
  TrendingUp,
  Trophy,
  Type,
  Zap,
} from 'lucide-react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import { ClosePanelButton } from './ClosePanelButton';
import { StatCard } from './StatCard';
import {
  CHART_BRAND_COLOR,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from './ui/chart';
import { toBcp47Locale } from '../locales/locale';
import {
  buildDocumentWritingSeries,
  computeAverageWordsPerWritingDay,
  computeDailyWordGains,
  detectInitialImport,
  findBiggestWritingDay,
  gainsExcludingInitialImport,
  findCurrentWritingStreak,
  findFavoriteWeekday,
  findLongestWritingGap,
  findLongestWritingStreak,
  findWordCountMilestones,
  findWordsWrittenOnDate,
  formatUtcWeekdayLabel,
  formatWritingGapRange,
  formatWritingStreakRange,
} from '../utils/document-writing-stats';

type WritingDayPoint = {
  writeDate: string;
  documentWordCount: number;
};

type DocumentStatsProps = {
  documentId: string;
  wordCount: number;
  readingTime: number;
  pageCount: number;
  selectedWordCount: number;
  selectedReadingTime: number;
  selectedPageCount: number;
  t: (key: string) => string;
  onClose?: () => void;
  hideHeading?: boolean;
};

function getUtcDateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function parseWriteDateToUtcTimestamp(writeDate: string): number {
  const [year, month, day] = writeDate.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

function formatChartDate(dateKey: string, locale: string): string {
  return formatUtcTimestamp(parseWriteDateToUtcTimestamp(dateKey), locale);
}

function formatUtcTimestamp(timestamp: number, locale: string): string {
  return new Intl.DateTimeFormat(toBcp47Locale(locale), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(timestamp));
}

function formatCountTemplate(count: number, singularKey: string, pluralKey: string, t: (key: string) => string): string {
  const template = count === 1 ? t(singularKey) : t(pluralKey);
  return template.replace('{count}', String(count));
}

function formatLocalizedNumber(value: number, locale: string): string {
  return value.toLocaleString(toBcp47Locale(locale));
}

export const DocumentStats: React.FC<DocumentStatsProps> = ({
  documentId,
  wordCount,
  readingTime,
  pageCount,
  selectedWordCount,
  selectedReadingTime,
  selectedPageCount,
  t,
  onClose,
  hideHeading = false,
}) => {
  const { token } = useAuth();
  const { locale } = useLocale();
  const [writingDays, setWritingDays] = useState<WritingDayPoint[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const todayKey = useMemo(() => getUtcDateKey(new Date()), []);

  useEffect(() => {
    if (!token || !documentId || documentId === 'new') {
      setWritingDays([]);
      setLoadingHistory(false);
      return;
    }

    let cancelled = false;

    const loadWritingDays = async (): Promise<void> => {
      setLoadingHistory(true);
      try {
        const response = await fetch(`${API_BASE}/api/documents/${documentId}/writing-stats`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch writing stats');
        }
        const data = await response.json();
        if (!cancelled) {
          setWritingDays(Array.isArray(data.days) ? data.days : []);
        }
      } catch (error) {
        console.error('Error loading document writing stats:', error);
        if (!cancelled) {
          setWritingDays([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingHistory(false);
        }
      }
    };

    void loadWritingDays();

    return () => {
      cancelled = true;
    };
  }, [documentId, token]);

  const writingSeries = useMemo(
    () => buildDocumentWritingSeries(writingDays, wordCount, todayKey),
    [writingDays, wordCount, todayKey]
  );

  const writeDates = useMemo(
    () => writingSeries.map((point) => point.writeDate),
    [writingSeries]
  );

  const dailyGains = useMemo(
    () => computeDailyWordGains(writingSeries),
    [writingSeries]
  );

  const initialImport = useMemo(
    () => detectInitialImport(dailyGains),
    [dailyGains]
  );

  const dailyGainsExcludingImport = useMemo(
    () => gainsExcludingInitialImport(dailyGains),
    [dailyGains]
  );

  const chartData = useMemo(
    () =>
      writingSeries.map((point) => ({
        writeDate: point.writeDate,
        timestamp: parseWriteDateToUtcTimestamp(point.writeDate),
        wordCount: point.wordCount,
      })),
    [writingSeries]
  );

  const chartTimeDomain = useMemo((): [number, number] | undefined => {
    if (chartData.length === 0) {
      return undefined;
    }
    const timestamps = chartData.map((point) => point.timestamp);
    const min = Math.min(...timestamps);
    const max = Math.max(...timestamps);
    if (min === max) {
      const oneDayMs = 24 * 60 * 60 * 1000;
      return [min - oneDayMs, max + oneDayMs];
    }
    return [min, max];
  }, [chartData]);

  const yAxisWidth = useMemo((): number => {
    const numberFormatter = new Intl.NumberFormat(toBcp47Locale(locale));
    const maxWordCount = chartData.reduce((max, point) => Math.max(max, point.wordCount), 0);
    const longestLabel = numberFormatter.format(maxWordCount);
    return Math.max(44, longestLabel.length * 8 + 16);
  }, [chartData, locale]);

  const currentWritingStreak = useMemo(
    () => findCurrentWritingStreak(writeDates, todayKey),
    [writeDates, todayKey]
  );

  const longestWritingStreak = useMemo(
    () => findLongestWritingStreak(writeDates),
    [writeDates]
  );

  const wordsWrittenToday = useMemo(
    () => findWordsWrittenOnDate(dailyGains, todayKey),
    [dailyGains, todayKey]
  );

  const biggestWritingDay = useMemo(
    () => findBiggestWritingDay(dailyGainsExcludingImport),
    [dailyGainsExcludingImport]
  );

  const averageWordsPerDay = useMemo(
    () => computeAverageWordsPerWritingDay(dailyGainsExcludingImport),
    [dailyGainsExcludingImport]
  );

  const longestPause = useMemo(
    () => findLongestWritingGap(writeDates),
    [writeDates]
  );

  const favoriteWeekday = useMemo(
    () => findFavoriteWeekday(writeDates),
    [writeDates]
  );

  const milestones = useMemo(
    () => findWordCountMilestones(writingSeries),
    [writingSeries]
  );

  const hasSelection = selectedWordCount > 0;
  const localizedChartConfig = useMemo(
    (): ChartConfig => ({
      wordCount: {
        label: t('word_plural'),
        color: CHART_BRAND_COLOR,
      },
    }),
    [t]
  );

  const loadingValue = loadingHistory ? '…' : undefined;

  return (
    <div style={{ minWidth: 0, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      {hideHeading ? null : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: 'var(--foreground)' }}>
            {t('documentStats.title')}
          </h2>
          {onClose ? (
            <ClosePanelButton onClick={onClose} ariaLabel={t('close')} />
          ) : null}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          icon={<Type className="h-4 w-4" aria-hidden />}
          label={t('word_plural')}
          value={wordCount.toLocaleString(toBcp47Locale(locale))}
        />
        <StatCard
          icon={<Clock className="h-4 w-4" aria-hidden />}
          label={t('reading_time')}
          value={`${readingTime}`}
          subvalue={readingTime === 1 ? t('min') : t('mins')}
        />
        <StatCard
          icon={<FileText className="h-4 w-4" aria-hidden />}
          label={pageCount === 1 ? t('page') : t('pages')}
          value={`${pageCount}`}
        />
      </div>

      {hasSelection ? (
        <div className="mt-4">
          <p className="mb-2 text-sm font-medium text-muted-foreground">{t('selection')}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard
              icon={<TextSelect className="h-4 w-4" aria-hidden />}
              label={t('word_plural')}
              value={selectedWordCount.toLocaleString(toBcp47Locale(locale))}
            />
            <StatCard
              icon={<Clock className="h-4 w-4" aria-hidden />}
              label={t('reading_time')}
              value={`${selectedReadingTime}`}
              subvalue={selectedReadingTime === 1 ? t('min') : t('mins')}
            />
            <StatCard
              icon={<FileText className="h-4 w-4" aria-hidden />}
              label={selectedPageCount === 1 ? t('page') : t('pages')}
              value={`${selectedPageCount}`}
            />
          </div>
        </div>
      ) : null}

      <div className="mt-8">
        <h3 className="mb-3 text-base font-semibold text-foreground">{t('documentStats.writingInsights')}</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            icon={<PenLine className="h-4 w-4" aria-hidden />}
            label={t('documentStats.wordsWrittenToday')}
            value={
              loadingValue
              ?? t('documentStats.wordsWrittenTodayValue').replace(
                '{count}',
                formatLocalizedNumber(wordsWrittenToday, locale)
              )
            }
          />
          <StatCard
            icon={<Zap className="h-4 w-4" aria-hidden />}
            label={t('documentStats.currentWritingStreak')}
            value={
              loadingValue
              ?? (currentWritingStreak
                ? formatCountTemplate(
                  currentWritingStreak.length,
                  'documentStats.longestWritingStreakDay',
                  'documentStats.longestWritingStreakDays',
                  t
                )
                : '0')
            }
            subvalue={
              loadingHistory || !currentWritingStreak
                ? undefined
                : t('documentStats.sinceDate').replace(
                  '{date}',
                  formatChartDate(currentWritingStreak.startDate, locale)
                )
            }
          />
          <StatCard
            icon={<Flame className="h-4 w-4" aria-hidden />}
            label={t('documentStats.longestWritingStreak')}
            value={
              loadingValue
              ?? (longestWritingStreak
                ? formatCountTemplate(
                  longestWritingStreak.length,
                  'documentStats.longestWritingStreakDay',
                  'documentStats.longestWritingStreakDays',
                  t
                )
                : '0')
            }
            subvalue={
              loadingHistory || !longestWritingStreak
                ? undefined
                : formatWritingStreakRange(longestWritingStreak, locale, formatChartDate)
            }
          />
          <StatCard
            icon={<CalendarDays className="h-4 w-4" aria-hidden />}
            label={t('documentStats.totalWritingDays')}
            value={loadingValue ?? String(writeDates.length)}
          />
          <StatCard
            icon={<TrendingUp className="h-4 w-4" aria-hidden />}
            label={t('documentStats.biggestWritingDay')}
            value={
              loadingValue
              ?? (biggestWritingDay
                ? t('documentStats.biggestWritingDayValue').replace(
                  '{count}',
                  formatLocalizedNumber(biggestWritingDay.gain, locale)
                )
                : '0')
            }
            subvalue={
              loadingHistory || !biggestWritingDay || biggestWritingDay.gain === 0
                ? undefined
                : formatChartDate(biggestWritingDay.writeDate, locale)
            }
          />
          <StatCard
            icon={<BarChart3 className="h-4 w-4" aria-hidden />}
            label={t('documentStats.averageWordsPerDay')}
            value={
              loadingValue
              ?? (averageWordsPerDay === null
                ? '0'
                : t('documentStats.averageWordsPerDayValue').replace(
                  '{count}',
                  formatLocalizedNumber(averageWordsPerDay, locale)
                ))
            }
          />
          <StatCard
            icon={<Moon className="h-4 w-4" aria-hidden />}
            label={t('documentStats.longestPause')}
            value={
              loadingValue
              ?? (longestPause
                ? formatCountTemplate(
                  longestPause.length,
                  'documentStats.pauseDay',
                  'documentStats.pauseDays',
                  t
                )
                : '0')
            }
            subvalue={
              loadingHistory || !longestPause
                ? undefined
                : formatWritingGapRange(longestPause, locale, formatChartDate)
            }
          />
          <StatCard
            icon={<CalendarDays className="h-4 w-4" aria-hidden />}
            label={t('documentStats.favoriteWeekday')}
            value={
              loadingValue
              ?? (favoriteWeekday
                ? formatUtcWeekdayLabel(favoriteWeekday.weekday, locale)
                : '—')
            }
            subvalue={
              loadingHistory || !favoriteWeekday
                ? undefined
                : formatCountTemplate(
                  favoriteWeekday.count,
                  'documentStats.favoriteWeekdayOnDay',
                  'documentStats.favoriteWeekdayOnDays',
                  t
                )
            }
          />
          {initialImport ? (
            <StatCard
              icon={<Import className="h-4 w-4" aria-hidden />}
              label={t('documentStats.initialImport')}
              value={t('documentStats.initialImportValue').replace(
                '{count}',
                formatLocalizedNumber(initialImport.wordCount, locale)
              )}
              subvalue={t('documentStats.initialImportOnDate').replace(
                '{date}',
                formatChartDate(initialImport.writeDate, locale)
              )}
            />
          ) : null}
        </div>
      </div>

      <div className="mt-8">
        <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-foreground">
          <Trophy className="h-4 w-4 text-primary" aria-hidden />
          {t('documentStats.milestones')}
        </h3>
        {loadingHistory ? (
          <p className="text-sm text-muted-foreground">{t('documentStats.loading')}</p>
        ) : milestones.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('documentStats.noMilestones')}</p>
        ) : (
          <ul className="space-y-2">
            {milestones.map((milestone) => (
              <li
                key={milestone.threshold}
                className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3 text-sm text-foreground"
              >
                {t('documentStats.milestoneEntry')
                  .replace('{count}', formatLocalizedNumber(milestone.threshold, locale))
                  .replace('{date}', formatChartDate(milestone.writeDate, locale))}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-8">
        <h3 className="mb-1 text-base font-semibold text-foreground">{t('documentStats.wordCountOverTime')}</h3>
        <p className="mb-4 text-sm text-muted-foreground">{t('documentStats.wordCountOverTimeHint')}</p>
        {loadingHistory ? (
          <div className="py-12 text-center text-sm text-muted-foreground">{t('documentStats.loading')}</div>
        ) : chartData.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-12 text-center text-sm text-muted-foreground">
            {t('documentStats.noHistory')}
          </div>
        ) : (
          <ChartContainer config={localizedChartConfig} className="aspect-[16/9] w-full max-h-[320px]">
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="timestamp"
                type="number"
                scale="time"
                domain={chartTimeDomain}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(value: number) => formatUtcTimestamp(value, locale)}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={yAxisWidth}
                tickFormatter={(value: number) => value.toLocaleString(toBcp47Locale(locale))}
              />
              <ChartTooltip
                cursor={{ stroke: 'var(--border)', strokeWidth: 1 }}
                content={
                  <ChartTooltipContent
                    labelFormatter={(_, payload) => {
                      const point = payload?.[0]?.payload as { writeDate?: string } | undefined;
                      if (!point?.writeDate) {
                        return '';
                      }
                      return formatChartDate(point.writeDate, locale);
                    }}
                  />
                }
              />
              <Line
                type="monotone"
                dataKey="wordCount"
                stroke="var(--color-wordCount)"
                strokeWidth={2}
                dot={{ r: 3, fill: 'var(--color-wordCount)' }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ChartContainer>
        )}
      </div>
    </div>
  );
};
